import React, { useContext, useEffect, useMemo } from "react";
import {
  Container,
  styled,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material"
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import FormLabeledTextField from "components/form/FormLabeledTextField";
import StyledButton from "components/button/StyledButton";
import { useNavigate } from "react-router-dom";
import { ProjectContext } from "context/ProjectContext";
import "../../components/button/StyledButton.css"
import { useGetEcomLatestQuery, useGetUserQuery } from "api/api";
import { useCreateUpdateProject } from "hooks/useCreateUpdateProject";
import { getErrorMessage } from "lib/utils/getErrorMessage";

const schema = z.object({
  a1_a3_gwp_kg: z.number({
    required_error: "A1-A3 is required",
    invalid_type_error: "A1-A3 must be a number",
  }).nonnegative("A1-A3 must be a non-negative number"),

  a4_gwp_kg: z.number({
    invalid_type_error: "A4 must be a number",
  }).nonnegative("A4 must be a non-negative number").nullable().optional(),

  a5_gwp_kg: z.number({
    invalid_type_error: "A5 must be a number",
  }).nonnegative("A5 must be a non-negative number").nullable().optional(),

  b1_b5_gwp_kg: z.number({
    invalid_type_error: "B1-B5 must be a number",
  }).nonnegative("B1-B5 must be a non-negative number").nullable().optional(),

  c1_c4_gwp_kg: z.number({
    invalid_type_error: "C1-C4 must be a number",
  }).nonnegative("C1-C4 must be a non-negative number").nullable().optional(),

  d_gwp_kg: z.number({
    invalid_type_error: "D must be a number",
  }).max(0, { message: "D must be a negative number or 0" }).nullable().optional(),

  biogenic_carbon_flow_kg: z.number({
    invalid_type_error: "Biogenic Carbon Flow must be a number",
  }).nonnegative("Biogenic Carbon Flow must be a non-negative number").nullable().optional(),
});

const AddProjectEmbodiedCarbonReporting: React.FC = () => {
  const navigate = useNavigate();

  const { data: userData } = useGetUserQuery();
  const { data: latestEcomData = [], isLoading: ecomIsLoading } = useGetEcomLatestQuery();

  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('ProjectContext error');
  }
  const { projectDetails, setProjectDetails, smqSubtotals } = context;

  const isSubmitted = projectDetails.is_submitted;

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      a1_a3_gwp_kg: projectDetails.a1_a3_gwp_kg,
      a4_gwp_kg: projectDetails.a4_gwp_kg,
      a5_gwp_kg: projectDetails.a5_gwp_kg,
      b1_b5_gwp_kg: projectDetails.b1_b5_gwp_kg,
      c1_c4_gwp_kg: projectDetails.c1_c4_gwp_kg,
      d_gwp_kg: projectDetails.d_gwp_kg,
      biogenic_carbon_flow_kg: projectDetails.biogenic_carbon_flow_kg,
    },
  });

  const { control, handleSubmit, getValues, watch, formState: { errors, isSubmitting } } = form;

  const { handleCreateUpdateProject, error, isLoading } = useCreateUpdateProject({ userData });

  const recommendedA1A3EmbodiedCarbon = smqSubtotals.Substructure + smqSubtotals.Superstructure;

  const watchedFields = watch(['a1_a3_gwp_kg', 'a4_gwp_kg', 'a5_gwp_kg', 'b1_b5_gwp_kg', 'c1_c4_gwp_kg']);

  useEffect(() => {
    const updatedProjectDetails = {
      ...projectDetails,
      ...getValues(),
    };
    setProjectDetails(updatedProjectDetails);
  }, watchedFields);

  const biogenicCarbon = useMemo(() => {
    const content = projectDetails.biogenic_carbon_content;

    if (content != null && !Number.isNaN(content)) {
      return `${content.toLocaleString(undefined, { maximumFractionDigits: 0 })} kgCO2e`;
    }

    return null;
  }, [projectDetails]);

  const handleSubmitForm = async () => {
    handleCreateUpdateProject({ formValues: getValues(), isSubmitting: true })
  };

  const handleSaveAndExit = async () => {
    handleCreateUpdateProject({ formValues: getValues() })
  };

  const handleClickBack = () => {
    setProjectDetails(prevDetails => ({ ...prevDetails, ...getValues() }));
    navigate('/add-project/7')
  };

  const inputLabels = [
    { name: "a1_a3_gwp_kg" as const, label: "A1-A3 (kgCO2e)", value: projectDetails["a1_a3_gwp_kg"], description: `For reference, the A1-A3 estimate developed from the structural material quantities you reported on steps 3-5 is: ${recommendedA1A3EmbodiedCarbon.toLocaleString()} kg CO2e` },
    { name: "a4_gwp_kg" as const, label: "A4 [optional] (kgCO2e)", value: projectDetails["a4_gwp_kg"], description: "" },
    { name: "a5_gwp_kg" as const, label: "A5 [optional] (kgCO2e)", value: projectDetails["a5_gwp_kg"], description: "" },
    { name: "b1_b5_gwp_kg" as const, label: "B1-B5 [optional] (kgCO2e)", value: projectDetails["b1_b5_gwp_kg"], description: "" },
    { name: "c1_c4_gwp_kg" as const, label: "C1-C4 [optional] (kgCO2e)", value: projectDetails["c1_c4_gwp_kg"], description: "" },
    { name: "d_gwp_kg" as const, label: "D [optional] (kgCO2e)", value: projectDetails["d_gwp_kg"], description: "" },
  ];

  return (
    <StyledContainer>
      <div style={{ width: '100%', height: '100%', background: 'white', boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12)', borderRadius: 5, padding: 20, paddingLeft: 41, paddingTop: 37 }}>
        <Box sx={{ width: "1100px" }}>
          <div style={{ width: '100%', height: '100%', color: '#1A1A1A', fontSize: 28, fontFamily: 'Helvetica Neue LT Std', fontWeight: '300', wordWrap: 'break-word', paddingBottom: 12 }}>Embodied Carbon Reporting</div>
          <div style={{ width: '100%', height: '100%', color: '#1A1A1A', fontSize: 16, fontFamily: 'Helvetica Neue LT Std', fontStyle: 'italic', wordWrap: 'break-word', paddingBottom: 12 }}>
            If you used a third party embodied carbon assessment tool, please indicate your results below. Enter values for A-D life cycle stages excluding biogenic carbon and enter biogenic carbon flow according to User Guide (<a href="/support" target="_blank">see Support</a>). If not, the A1-A3 GWP for this project calculated from steps 3-5 will be submitted as the Embodied Carbon for this project entry.
          </div>
          <div style={{ width: '536px', height: '100%', background: '#D9EAF5', color: '#1A1A1A', fontSize: 16, fontFamily: 'Helvetica Neue LT Std', fontWeight: '700', wordWrap: 'break-word', marginBottom: 12, paddingLeft: 8 }}>
            Embodied Carbon (kgCO2e)
          </div>
          <form onSubmit={handleSubmit(handleSubmitForm)}>
            {inputLabels.map((inputLabel) => (
              <Controller
                key={inputLabel.name}
                name={inputLabel.name}
                control={control}
                defaultValue={inputLabel.value}
                render={({ field }) => (
                  <Box>
                    <div style={{ display: 'flex', paddingTop: 12 }}>
                      <div style={{ width: '250px' }} className="header-row column-two row-title">{inputLabel.label}</div>
                      <div style={{ display: 'flex', width: '250px', marginLeft: 10, alignSelf: 'center' }}>
                        <FormLabeledTextField
                          {...field}
                          label="kgCO2e"
                          error={!!errors[inputLabel.name]}
                          helperText={errors[inputLabel.name]?.message}
                          disabled={isSubmitting}
                          updateFunction={(value) => field.onChange(value === '' ? null : Number(value))}
                        />
                      </div>
                      <div style={{ width: '600px', marginLeft: 16 }} className="header-row regular-text column-two row-title">{inputLabel.description}</div>
                    </div>
                  </Box>
                )}
              />
            ))}
            <div style={{ display: 'flex', paddingTop: 12 }}>
              <div style={{ width: '250px' }} className="header-row column-two row-title">Total Embodied Carbon (excluding D)</div>
              <div style={{ display: 'flex', width: '250px', marginLeft: 10, alignSelf: 'center', justifyContent: 'right' }}>
                {(projectDetails.total_gwp_kg_exclude_d ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} kgCO2e
              </div>
            </div>
            <div style={{ display: 'flex', paddingTop: 12 }}>
              <div style={{ width: '250px' }} className="header-row column-two row-title">Embodied Carbon Intensity (excluding D)</div>
              <div style={{ display: 'flex', width: '250px', marginLeft: 10, alignSelf: 'center', justifyContent: 'right' }}>
                {(projectDetails.total_intensity_exclude_d ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} kgCO2e/m2
              </div>
            </div>
            <div style={{ display: 'flex', paddingTop: 12 }}>
              <div style={{ width: '250px' }} className="header-row column-two row-title">Biogenic Carbon Content</div>
              <div style={{ display: 'flex', width: '250px', marginLeft: 10, alignSelf: 'center', justifyContent: 'right' }}>
                {biogenicCarbon ?? <CircularProgress size={16} />}
              </div>
              <div style={{ width: '600px', marginLeft: 16 }} className="header-row regular-text column-two row-title">
                For reference, this biogenic carbon content was developed from the structural material quantities you reported in steps 3-5.
              </div>
            </div>
            <Controller
              name="biogenic_carbon_flow_kg"
              control={control}
              defaultValue={projectDetails.biogenic_carbon_flow_kg}
              render={({ field }) => (
                <Box>
                  <div style={{ display: 'flex', paddingTop: 12 }}>
                    <div style={{ width: '250px' }} className="header-row column-two row-title">Biogenic Carbon Flow [optional] (kgCO2e)</div>
                    <div style={{ display: 'flex', width: '250px', marginLeft: 10, alignSelf: 'center' }}>
                      <FormLabeledTextField
                        {...field}
                        label="kgCO2e"
                        error={!!errors.biogenic_carbon_flow_kg}
                        helperText={errors.biogenic_carbon_flow_kg?.message}
                        disabled={isSubmitting}
                        updateFunction={(value) => field.onChange(value === '' ? null : Number(value))}
                      />
                    </div>
                    <div style={{ width: '600px', marginLeft: 16 }} className="header-row regular-text column-two row-title">
                      Biogenic Carbon Flow is different from the Biogenic Carbon Content. See User Guide for how to derive this value from third party embodied carbon assessment tools.
                    </div>
                  </div>
                </Box>
              )}
            />
            <Box sx={{ display: "flex", mt: 3, justifyContent: "flex-start", alignItems: 'center' }}>
              {!isSubmitted && <StyledButton label="Save &amp; Exit" type="outlined" onClick={handleSubmit(handleSaveAndExit)} />}
              <StyledButton label="Back" type="outlined" onClick={handleClickBack} />
              <StyledButton
                label={isSubmitted ? "Save & Exit" : "Submit"}
                type="filled"
                onClick={handleSubmit(isSubmitted ? handleSaveAndExit : handleSubmitForm)}
              />
              {isLoading && (
                <CircularProgress
                  size={16}
                />
              )}
            </Box>
            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {getErrorMessage(error)}
              </Alert>
            )}
          </form>
        </Box>
      </div>
    </StyledContainer>
  );
}

const StyledContainer = styled(Container)({
  marginBottom: 24,
});

export default AddProjectEmbodiedCarbonReporting;